import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import client from '../lib/feathers';

// Dashboard "Needs Review" panel — one glance at how many items are waiting in
// each admin review queue, so you know when there's cleanup to do without opening
// every report. Counts come from admin/review-queue-summary (one call).
//
// Excluded: "Wines Needing Review" is acted on by INDUSTRY users (listing owners),
// not the admin team — we can't drive it down, so it's noise here. Zero-count
// queues are hidden by default with a "Show all" expander to reveal the rest.
const EXCLUDED_KEYS = new Set(['wines-needing-review']);

export default function ReviewQueuePanel() {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    client.service('admin/review-queue-summary').find()
      .then(setData)
      .catch(() => setFailed(true));
  }, []);

  if (failed || !data) return null;

  // Drop non-actionable queues; recompute the total from what's left (the API's
  // total still counts the excluded ones).
  const queues = (data.queues || []).filter((q) => !EXCLUDED_KEYS.has(q.key));
  const total = queues.reduce((s, q) => s + (q.count || 0), 0);
  const active = queues.filter((q) => typeof q.count === 'number' && q.count > 0);
  const emptyCount = queues.length - active.length;
  const shown = expanded ? queues : active;

  return (
    <div className="bg-white shadow rounded-lg p-4 my-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold text-gray-900">Needs Review</h3>
        <span className="text-sm text-gray-500">
          {total} item{total === 1 ? '' : 's'} waiting
        </span>
      </div>

      {active.length === 0 && !expanded ? (
        <p className="text-sm text-gray-500 py-1">Nothing needs review right now. 🎉</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {shown.map((q) => {
            const to = q.href.replace('./', '/admin/');
            const has = typeof q.count === 'number' && q.count > 0;
            return (
              <li key={q.key}>
                <NavLink
                  to={to}
                  className="flex items-center justify-between py-2 px-2 -mx-2 text-sm rounded hover:bg-gray-50"
                >
                  <span className={has ? 'text-gray-900 font-medium' : 'text-gray-500'}>{q.label}</span>
                  {q.count === null ? (
                    <span className="text-gray-300 text-xs">—</span>
                  ) : (
                    <span className={`tabular-nums text-xs px-2 py-0.5 rounded-full ${has ? 'bg-primary text-white' : 'text-gray-400'}`}>
                      {q.count}
                    </span>
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>
      )}

      {emptyCount > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 text-xs text-gray-500 hover:text-gray-700"
        >
          {expanded ? 'Show less' : `Show all (${emptyCount} empty)`}
        </button>
      )}
    </div>
  );
}
